import Config from '@lib/config';

export const isWeb = true;

/**
 * JSAD-7145. Braze SDK Authentication failure, normalised across the two SDKs.
 */
export interface SdkAuthError {
    errorCode: number;
    userId?: string;
}

/**
 * JSAD-7144. Standard profile attributes the app writes right after `changeUser`.
 * Every field is optional: an absent field leaves the Braze profile untouched.
 */
export interface BrazeUserAttributes {
    email?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
}

/**
 * The web SDK's `User`, reached through `braze.getUser()`. Setters return whether the
 * update was enqueued (false on validation failure, e.g. a non RFC-5322 email).
 */
export interface BrazeUser {
    setEmail: (email: string | null) => boolean;
    setFirstName: (firstName: string | null) => boolean;
    setLastName: (lastName: string | null) => boolean;
    setPhoneNumber: (phoneNumber: string | null) => boolean;
}

export interface BrazeInstance {
    logCustomEvent: (eventName: string, properties?: Record<string, any>) => void;
    logPurchase: (
        productId: string,
        price: string | number,
        currency: string,
        quantity: number,
        properties?: Record<string, any>
    ) => void | boolean;
    /**
     * `signature` is the Braze SDK Authentication JWT issued by our backend. It only
     * has an effect while the `enableSdkAuthentication` init option is set.
     */
    changeUser: (userId: string, signature?: string) => void;
    setSdkAuthenticationSignature: (signature: string) => void;
    openSession?: () => void;
    subscribeToSdkAuthenticationFailures?: (
        subscriber: (error: {
            errorCode: number;
            reason?: string;
            userId?: string;
            signature?: string;
        }) => void
    ) => string | undefined;
    // Undefined until the SDK is initialised.
    getUser?: () => BrazeUser | undefined;
}

/*
 * Web-specific implementation to keep Turbopack from traversing native Braze modules.
 */
export const getBrazeInstance = async (): Promise<BrazeInstance | undefined> => {
    const braze = await import('@braze/web-sdk');
    const isInitialised = braze.initialize(Config.brazeConfig.webApiKey, {
        baseUrl: Config.brazeConfig.baseUrl,
        enableLogging: Config.brazeConfig.enableLogging,
        // JSAD-7145. Must be present on the FIRST initialize call: a later one
        // returns true but discards its options.
        enableSdkAuthentication: true
    });

    if (!isInitialised) {
        return;
    }

    return braze as BrazeInstance;
};

/**
 * Subscribe to Braze SDK Authentication failures (expired or rejected signature).
 */
export const subscribeToSdkAuthErrors = (
    braze: BrazeInstance,
    handler: (error: SdkAuthError) => void
) => {
    braze.subscribeToSdkAuthenticationFailures?.((error) =>
        handler({ errorCode: error.errorCode, userId: error.userId })
    );
};

type AttributeSetters = Partial<Record<keyof BrazeUser, (value: string | null) => unknown>>;

// Only present fields are written. A setter is never called with null or '', since null
// unsets the attribute in Braze and '' would blank a name Braze already holds.
const applyAttributes = (target: AttributeSetters, attrs: BrazeUserAttributes) => {
    if (attrs.email) {
        target.setEmail?.(attrs.email);
    }
    if (attrs.firstName) {
        target.setFirstName?.(attrs.firstName);
    }
    if (attrs.lastName) {
        target.setLastName?.(attrs.lastName);
    }
    if (attrs.phone) {
        target.setPhoneNumber?.(attrs.phone);
    }
};

/**
 * JSAD-7144. Write the standard profile attributes for the CURRENT Braze user.
 *
 * Must run after `changeUser`: the SDK keys attribute updates on whichever user id is
 * current when they are enqueued, so an earlier write lands on the previous or anonymous
 * profile.
 */
export const setBrazeUserAttributes = (braze: BrazeInstance, attrs: BrazeUserAttributes) => {
    const user = braze.getUser?.();
    if (user) {
        applyAttributes(user, attrs);
    }
};
